import { __ } from "@wordpress/i18n";
import { useBlockProps, InspectorControls, RichText } from "@wordpress/block-editor";
import { 
    PanelBody, 
    SelectControl, 
    RangeControl, 
    ToggleControl,
    TextControl,
    TextareaControl,
    Button,
    Card,
    CardBody,
    CardHeader,
    CheckboxControl,
    Spinner,
    __experimentalNumberControl as NumberControl 
} from "@wordpress/components";
import { useSelect } from "@wordpress/data";
import { useState, useMemo } from "@wordpress/element";

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: "testimonials-block" });
    
    const {
        mode,
        testimonialType,
        testimonialCount,
        testimonialLayout,
        testimonialRandomOrder,
        testimonialShowDots,
        testimonialShowArrows,
        testimonialAutoplay,
        testimonialAutoplaySpeed,
        heading,
        subheading,
        backgroundColor,
        textColor,
        testimonialSource,
        staticTestimonials,
        testimonialFilterType,
        testimonialCategories,
        testimonialIds
    } = attributes;

    // State for category filter
    const [searchTerm, setSearchTerm] = useState("");

    // Fetch all testimonial categories (assuming they exist)
    const testimonialCategoriesData = useSelect((select) => {
        return select("core").getEntityRecords("taxonomy", "testimonial-category", {
            per_page: -1,
            status: "publish"
        });
    }, []);

    // Fetch all testimonial posts for ID selection
    const testimonialPosts = useSelect((select) => {
        return select("core").getEntityRecords("postType", "reviews", {
            per_page: -1,
            status: "publish"
        });
    }, []);

    const isLoadingCategories = testimonialCategoriesData === null;
    const isLoadingPosts = testimonialPosts === null;

    // Create options for category selection
    const categoryOptions = testimonialCategoriesData
        ? testimonialCategoriesData.map((category) => ({
            label: category.name,
            value: String(category.id)
        }))
        : [];

    // Create options for testimonial ID selection
    const testimonialOptions = testimonialPosts
        ? testimonialPosts.map((post) => ({
            label: post.title?.rendered || `#${post.id}`,
            value: String(post.id)
        }))
        : [];

    // Filter options based on search term
    const filteredCategoryOptions = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return categoryOptions;
        return categoryOptions.filter((opt) =>
            opt.label.toLowerCase().includes(q)
        );
    }, [categoryOptions, searchTerm]);

    const filteredTestimonialOptions = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return testimonialOptions;
        return testimonialOptions.filter((opt) =>
            opt.label.toLowerCase().includes(q)
        );
    }, [testimonialOptions, searchTerm]);

    // Handle category selection toggle
    const toggleCategorySelect = (value) => {
        const has = testimonialCategories.includes(value);
        if (has) {
            setAttributes({ testimonialCategories: testimonialCategories.filter((v) => v !== value) });
        } else {
            setAttributes({ testimonialCategories: [...testimonialCategories, value] });
        }
    };

    // Handle testimonial ID selection toggle
    const toggleTestimonialSelect = (value) => {
        const has = testimonialIds.includes(value);
        if (has) {
            setAttributes({ testimonialIds: testimonialIds.filter((v) => v !== value) });
        } else {
            setAttributes({ testimonialIds: [...testimonialIds, value] });
        }
    };

    // Get dummy testimonials for preview
    const getDummyTestimonials = (count = 3) => {
        const pool = [
            {
                text: "Thomas was able to help my family on short notice. He patiently explained what we needed and the method for cleaning. They worked quietly under strained conditions (death of a family member) and the room was clean, sanitized and smelled fresh. We will indeed use this company again.",
                author: "JACKIE T. - Coral Gables, FL"
            },
            {
                text: "I want to give a major shout out to the guys in Beaverton/Portland, Oregon. We had a pipe break in our very large crawl space. It caused so much water and mold damage that our entire bottom 2 floors had to be removed down to the subfloor.",
                author: "KATIE - Portland, Oregon"
            },
            {
                text: "PuroClean exceeded our expectations during a very stressful time. The team was professional, efficient, and communicated with us every step of the way. Highly recommend their services!",
                author: "MICHAEL R. - Tampa, FL"
            },
            {
                text: "The response time was incredible - they were at our property within 2 hours of our call. The crew was professional, courteous, and explained every step of the process. Our home was restored to better than before condition.",
                author: "SARAH M. - Orlando, FL"
            },
            {
                text: "After our basement flooded, PuroClean handled everything from water extraction to complete restoration. They worked with our insurance company directly and made the whole process stress-free for us.",
                author: "DAVID L. - Tampa Bay, FL"
            }
        ];
        
        return pool.slice(0, Math.min(count, pool.length));
    };

    const testimonials = testimonialSource === 'static' && staticTestimonials.length > 0 
        ? staticTestimonials.slice(0, testimonialCount) 
        : getDummyTestimonials(testimonialCount);

    // Functions for managing static testimonials
    const addStaticTestimonial = () => {
        const newTestimonial = {
            text: '',
            author: '',
            location: '',
            rating: 5
        };
        setAttributes({ 
            staticTestimonials: [...staticTestimonials, newTestimonial] 
        });
    };

    const updateStaticTestimonial = (index, field, value) => {
        const updatedTestimonials = [...staticTestimonials];
        updatedTestimonials[index] = {
            ...updatedTestimonials[index],
            [field]: value
        };
        setAttributes({ staticTestimonials: updatedTestimonials });
    };

    const removeStaticTestimonial = (index) => {
        const updatedTestimonials = staticTestimonials.filter((_, i) => i !== index);
        setAttributes({ staticTestimonials: updatedTestimonials });
    };

    const renderSlidesMode = () => {
        return (
            <div className="testimonials-slides-mode">
                {/* Header */}
                <div className="text-center mb-12">
                    <RichText
                        tagName="h2"
                        placeholder={__('Testimonials heading...', 'puroclean')}
                        value={heading}
                        onChange={(value) => setAttributes({ heading: value })}
                        className="text-4xl font-bold mb-4"
                    />
                    <RichText
                        tagName="p"
                        placeholder={__('Subheading...', 'puroclean')}
                        value={subheading}
                        onChange={(value) => setAttributes({ subheading: value })}
                        className="text-xl text-red-600 font-semibold"
                    />
                </div>

                {/* Slides Layout - Similar to steps slider */}
                <div className="flex items-center gap-8">
                    {/* Left Arrow */}
                    <div className="flex-shrink-0">
                        <button 
                            type="button" 
                            className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                            onClick={(e) => e.preventDefault()}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>

                    {/* Testimonials Container */}
                    <div className="flex-1">
                        <div className="flex items-stretch gap-4 h-[420px]">
                            {testimonials.map((testimonial, index) => (
                                <div 
                                    key={index}
                                    className={`testimonial-slide transition-all duration-500 ease-in-out ${
                                        index === 0 ? 'flex-1' : 'w-28'
                                    }`}
                                >
                                    <div className={`rounded-[10px] p-6 cursor-pointer h-full flex flex-col ${
                                        index === 0 
                                            ? 'justify-start bg-secondary-900 text-white' 
                                            : 'bg-white text-secondary-900 hover:bg-gray-50 justify-center'
                                    }`}>
                                        
                                        {/* Minimized State: Just number */}
                                        {index !== 0 && (
                                            <div className="text-center">
                                                <h4 className="text-xl font-semibold text-secondary-900">
                                                    {index + 1}
                                                </h4>
                                            </div>
                                        )}
                                        
                                        {/* Expanded State: Full Content */}
                                        {index === 0 && (
                                            <div className="opacity-100">
                                                {/* Quotes Icon */}
                                                <div className="relative -mt-2 ml-7 mb-6 z-10">
                                                    <svg 
                                                        xmlns="http://www.w3.org/2000/svg" 
                                                        width="96" 
                                                        height="96" 
                                                        viewBox="0 0 117.809 121.505"
                                                        className="w-20 h-20"
                                                        aria-hidden="true"
                                                    >
                                                        <path 
                                                            d="M79.654-586.495H131.4v-63.243H111.686c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L110.865-706l-10.677,14.373C81.707-666.576,79.654-660,79.654-630.847Zm-63.654,0H67.744v-63.243H48.032c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L47.211-706,36.534-691.627C18.053-666.576,16-660,16-630.847Z" 
                                                            transform="translate(-15 707)" 
                                                            fill="none" 
                                                            stroke="#f6f6f6" 
                                                            strokeLinecap="round" 
                                                            strokeLinejoin="round" 
                                                            strokeWidth="2"
                                                        />
                                                    </svg>
                                                </div>
                                                
                                                <blockquote className="border-0 m-0 p-0">
                                                    <p className="italic text-xl leading-7 mb-6 text-white">
                                                        "{testimonial.text}"
                                                    </p>
                                                    <cite className="not-italic font-semibold text-lg text-white">
                                                        {testimonial.author}{testimonial.location && testimonial.location !== testimonial.author ? ` - ${testimonial.location}` : ''}
                                                    </cite>
                                                </blockquote>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Arrow */}
                    <div className="flex-shrink-0">
                        <button 
                            type="button" 
                            className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                            onClick={(e) => e.preventDefault()}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderCarouselMode = () => {
        return (
            <div className="testimonials-carousel-mode">
                {/* Header */}
                <div className="text-center mb-12">
                    <RichText
                        tagName="h2"
                        placeholder={__('Testimonials heading...', 'puroclean')}
                        value={heading}
                        onChange={(value) => setAttributes({ heading: value })}
                        className="text-4xl font-bold mb-4"
                    />
                    <RichText
                        tagName="p"
                        placeholder={__('Subheading...', 'puroclean')}
                        value={subheading}
                        onChange={(value) => setAttributes({ subheading: value })}
                        className="text-xl text-red-600 font-semibold"
                    />
                </div>

                {/* Testimonials Carousel Preview */}
                <div className="bg-secondary-900 text-white rounded-lg p-8 min-h-[300px] flex items-center justify-center">
                    <div className="text-center">
                        <div className="mb-6">
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="80" 
                                height="80" 
                                viewBox="0 0 117.809 121.505"
                                className="w-20 h-20 mx-auto mb-4"
                                aria-hidden="true"
                            >
                                <path 
                                    d="M79.654-586.495H131.4v-63.243H111.686c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L110.865-706l-10.677,14.373C81.707-666.576,79.654-660,79.654-630.847Zm-63.654,0H67.744v-63.243H48.032c1.232-10.678,4.928-18.891,14.373-38.192l5.749-11.909L47.211-706,36.534-691.627C18.053-666.576,16-660,16-630.847Z" 
                                    transform="translate(-15 707)" 
                                    fill="none" 
                                    stroke="#f6f6f6" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth="2"
                                />
                            </svg>
                        </div>
                        <blockquote className="border-0 m-0 p-0">
                            <p className="italic text-xl leading-7 mb-6 text-white">
                                "{testimonials[0]?.text || 'Sample testimonial text will appear here...'}"
                            </p>
                            <cite className="not-italic font-semibold text-lg text-white">
                                {testimonials[0] ? (
                                    `${testimonials[0].author}${testimonials[0].location && testimonials[0].location !== testimonials[0].author ? ` - ${testimonials[0].location}` : ''}`
                                ) : 'CUSTOMER NAME - Location'}
                            </cite>
                        </blockquote>
                        <div className="mt-6 text-sm opacity-75">
                            Carousel Mode Preview - {testimonialCount} testimonials
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <InspectorControls>
                <PanelBody title={__('Testimonial Source', 'puroclean')} initialOpen={true}>
                    <SelectControl
                        label={__('Source', 'puroclean')}
                        value={testimonialSource}
                        options={[
                            { label: __('Dynamic (from database)', 'puroclean'), value: 'dynamic' },
                            { label: __('Static (custom entries)', 'puroclean'), value: 'static' }
                        ]}
                        onChange={(value) => setAttributes({ testimonialSource: value })}
                        help={__('Choose whether to use testimonials from the database or custom static entries', 'puroclean')}
                    />
                </PanelBody>

                {testimonialSource === 'static' && (
                    <PanelBody title={__('Static Testimonials', 'puroclean')} initialOpen={true}>
                        {staticTestimonials.length === 0 ? (
                            <p>{__('No static testimonials added yet.', 'puroclean')}</p>
                        ) : (
                            staticTestimonials.map((testimonial, index) => (
                                <Card key={index} style={{ marginBottom: '16px' }}>
                                    <CardHeader>
                                        <span>{__('Testimonial', 'puroclean')} {index + 1}</span>
                                        <Button 
                                            isDestructive 
                                            variant="link" 
                                            onClick={() => removeStaticTestimonial(index)}
                                            style={{ marginLeft: 'auto' }}
                                        >
                                            {__('Remove', 'puroclean')}
                                        </Button>
                                    </CardHeader>
                                    <CardBody>
                                        <TextareaControl
                                            label={__('Testimonial Text', 'puroclean')}
                                            value={testimonial.text}
                                            onChange={(value) => updateStaticTestimonial(index, 'text', value)}
                                            rows={4}
                                        />
                                        <TextControl
                                            label={__('Author Name', 'puroclean')}
                                            value={testimonial.author}
                                            onChange={(value) => updateStaticTestimonial(index, 'author', value)}
                                            placeholder={__('e.g., John Smith', 'puroclean')}
                                        />
                                        <TextControl
                                            label={__('Location', 'puroclean')}
                                            value={testimonial.location}
                                            onChange={(value) => updateStaticTestimonial(index, 'location', value)}
                                            placeholder={__('e.g., Tampa, FL', 'puroclean')}
                                        />
                                        <RangeControl
                                            label={__('Rating', 'puroclean')}
                                            value={testimonial.rating}
                                            onChange={(value) => updateStaticTestimonial(index, 'rating', value)}
                                            min={1}
                                            max={5}
                                        />
                                    </CardBody>
                                </Card>
                            ))
                        )}
                        <Button 
                            variant="secondary" 
                            onClick={addStaticTestimonial}
                            style={{ marginTop: '16px' }}
                        >
                            {__('Add Testimonial', 'puroclean')}
                        </Button>
                    </PanelBody>
                )}

                <PanelBody title={__('Display Mode', 'puroclean')} initialOpen={true}>
                    <SelectControl
                        label={__('Mode', 'puroclean')}
                        value={mode}
                        options={[
                            { label: __('Carousel', 'puroclean'), value: 'carousel' },
                            { label: __('Slides', 'puroclean'), value: 'slides' }
                        ]}
                        onChange={(value) => setAttributes({ mode: value })}
                        help={__('Choose how testimonials are displayed', 'puroclean')}
                    />
                </PanelBody>

                <PanelBody title={__('Content Settings', 'puroclean')} initialOpen={true}>
                    <TextControl
                        label={__('Heading', 'puroclean')}
                        value={heading}
                        onChange={(value) => setAttributes({ heading: value })}
                    />
                    <TextControl
                        label={__('Subheading', 'puroclean')}
                        value={subheading}
                        onChange={(value) => setAttributes({ subheading: value })}
                    />
                </PanelBody>

                <PanelBody title={__('Testimonials Settings', 'puroclean')} initialOpen={true}>
                    {testimonialSource === 'dynamic' && (
                        <>
                            <SelectControl
                                label={__('Testimonial Type', 'puroclean')}
                                value={testimonialType}
                                options={[
                                    { label: __('Client Reviews', 'puroclean'), value: 'client' },
                                    { label: __('Employee Reviews', 'puroclean'), value: 'employee' },
                                    { label: __('All Reviews', 'puroclean'), value: 'all' }
                                ]}
                                onChange={(value) => setAttributes({ testimonialType: value })}
                                help={__('Choose which type of testimonials to display', 'puroclean')}
                            />

                            <ToggleControl
                                label={__('Random Order', 'puroclean')}
                                checked={testimonialRandomOrder}
                                onChange={(value) => setAttributes({ testimonialRandomOrder: value })}
                                help={__('Display testimonials in random order', 'puroclean')}
                            />
                        </>
                    )}

                    <RangeControl
                        label={__('Number of Testimonials', 'puroclean')}
                        value={testimonialCount}
                        onChange={(value) => setAttributes({ testimonialCount: value })}
                        min={1}
                        max={testimonialSource === 'static' ? staticTestimonials.length || 1 : 10}
                        help={testimonialSource === 'static' 
                            ? __('How many static testimonials to display', 'puroclean')
                            : __('How many testimonials to fetch and display', 'puroclean')}
                    />

                    {mode === 'carousel' && (
                        <>
                            <SelectControl
                                label={__('Layout Style', 'puroclean')}
                                value={testimonialLayout}
                                options={[
                                    { label: __('Card (Default)', 'puroclean'), value: 'card' },
                                    { label: __('Minimal', 'puroclean'), value: 'minimal' },
                                    { label: __('Featured', 'puroclean'), value: 'featured' }
                                ]}
                                onChange={(value) => setAttributes({ testimonialLayout: value })}
                                help={__('Choose the visual style for testimonials', 'puroclean')}
                            />

                            <ToggleControl
                                label={__('Show Navigation Dots', 'puroclean')}
                                checked={testimonialShowDots}
                                onChange={(value) => setAttributes({ testimonialShowDots: value })}
                                help={__('Show pagination dots below testimonials', 'puroclean')}
                            />

                            <ToggleControl
                                label={__('Show Navigation Arrows', 'puroclean')}
                                checked={testimonialShowArrows}
                                onChange={(value) => setAttributes({ testimonialShowArrows: value })}
                                help={__('Show arrow navigation buttons', 'puroclean')}
                            />

                            <ToggleControl
                                label={__('Enable Autoplay', 'puroclean')}
                                checked={testimonialAutoplay}
                                onChange={(value) => setAttributes({ testimonialAutoplay: value })}
                                help={__('Automatically rotate through testimonials', 'puroclean')}
                            />

                            {testimonialAutoplay && (
                                <NumberControl
                                    label={__('Autoplay Speed (ms)', 'puroclean')}
                                    value={testimonialAutoplaySpeed}
                                    onChange={(value) => setAttributes({ testimonialAutoplaySpeed: parseInt(value) || 5000 })}
                                    min={1000}
                                    max={15000}
                                    step={500}
                                    help={__('Time between slides in milliseconds', 'puroclean')}
                                />
                            )}
                        </>
                    )}
                </PanelBody>

                {testimonialSource === 'dynamic' && (
                    <PanelBody title={__('Filter Testimonials', 'puroclean')} initialOpen={false}>
                        <SelectControl
                            label={__('Filter Type', 'puroclean')}
                            value={testimonialFilterType}
                            options={[
                                { label: __('Categories', 'puroclean'), value: 'categories' },
                                { label: __('Specific IDs', 'puroclean'), value: 'ids' }
                            ]}
                            onChange={(value) => setAttributes({ testimonialFilterType: value })}
                            help={__('Choose how to filter testimonials', 'puroclean')}
                        />

                        {testimonialFilterType === 'categories' && (
                            <div style={{ marginTop: 12 }}>
                                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                                    {__("Select Categories", "puroclean")} ({testimonialCategories.length} selected)
                                </label>

                                <TextControl
                                    placeholder={__("Search categories…", "puroclean")}
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                />

                                {isLoadingCategories && <Spinner />}

                                {!isLoadingCategories && (
                                    <div
                                        style={{
                                            maxHeight: 240,
                                            overflow: "auto",
                                            border: "1px solid #ddd",
                                            borderRadius: 4,
                                            padding: 8,
                                            marginTop: 8,
                                        }}
                                    >
                                        {filteredCategoryOptions.length === 0 && (
                                            <p style={{ margin: 0, color: "#666" }}>
                                                {__("No categories found", "puroclean")}
                                            </p>
                                        )}

                                        {filteredCategoryOptions.map((opt) => (
                                            <CheckboxControl
                                                key={opt.value}
                                                label={opt.label}
                                                checked={testimonialCategories.includes(opt.value)}
                                                onChange={() => toggleCategorySelect(opt.value)}
                                            />
                                        ))}
                                    </div>
                                )}

                                <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                                    {__("Select categories to filter testimonials by.", "puroclean")}
                                </p>
                            </div>
                        )}

                        {testimonialFilterType === 'ids' && (
                            <div style={{ marginTop: 12 }}>
                                <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
                                    {__("Select Specific Testimonials", "puroclean")} ({testimonialIds.length} selected)
                                </label>

                                <TextControl
                                    placeholder={__("Search testimonials…", "puroclean")}
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                />

                                {isLoadingPosts && <Spinner />}

                                {!isLoadingPosts && (
                                    <div
                                        style={{
                                            maxHeight: 240,
                                            overflow: "auto",
                                            border: "1px solid #ddd",
                                            borderRadius: 4,
                                            padding: 8,
                                            marginTop: 8,
                                        }}
                                    >
                                        {filteredTestimonialOptions.length === 0 && (
                                            <p style={{ margin: 0, color: "#666" }}>
                                                {__("No testimonials found", "puroclean")}
                                            </p>
                                        )}

                                        {filteredTestimonialOptions.map((opt) => (
                                            <CheckboxControl
                                                key={opt.value}
                                                label={opt.label}
                                                checked={testimonialIds.includes(opt.value)}
                                                onChange={() => toggleTestimonialSelect(opt.value)}
                                            />
                                        ))}
                                    </div>
                                )}

                                <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                                    {__("Select specific testimonials to display.", "puroclean")}
                                </p>
                            </div>
                        )}
                    </PanelBody>
                )}
            </InspectorControls>

            <div {...blockProps}>
                {mode === 'slides' ? renderSlidesMode() : renderCarouselMode()}
            </div>
        </>
    );
} 